import { CSSProperties, ReactNode } from 'react';
import { FormattedDate, FormattedMessage, FormattedTime } from 'react-intl';
import styled, { css } from 'styled-components';

import { Link } from '@sorare/routing';

import {
  GameCoverageStatus,
  GameStatus,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import defaultShield from '@sorare/core/src/assets/club/shield_none.png';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BodyS, LabelS } from '@sorare/core/src/atoms/typography';
import { LiveIndicator } from '@sorare/core/src/atoms/ui/LiveIndicator';
import { GameCoverageBadge } from '@sorare/core/src/components/stats/GameCoverageBadge';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { isGameFinished, isGameLive } from 'lib/so5';

const rootStyle = css`
  position: relative;
  isolation: isolate;
  padding: var(--intermediate-unit) var(--double-unit);
  background: var(--game-background, var(--c-nd-50));
  border: 1px solid var(--c-nd-150-opaque);
  border-radius: var(--double-unit);
  overflow: hidden;
`;
const ButtonRoot = styled(ButtonBase)`
  width: 100%;
  display: block;
  ${rootStyle}
`;
const Root = styled.div`
  ${rootStyle}
`;
const Overlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(30px);
`;
const Relative = styled(Vertical)`
  position: relative;
  isolation: isolate;
`;
const Metadata = styled(Horizontal)`
  @media ${laptopAndAbove} {
    justify-content: space-between;
  }
`;
const StyledHorizontal = styled(Horizontal).attrs({ center: true, gap: 3 })`
  text-align: center;

  & > * {
    flex-basis: 130px;
  }
`;
const Logo = styled.img`
  height: var(--triple-unit);
`;
const Badge = styled.span`
  padding: 2px var(--unit);
  border-radius: var(--half-unit);
  background: var(--c-nd-150);
`;
const NoWrapBodyS = styled(BodyS)`
  white-space: nowrap;
`;
const Metadatas = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-areas: 'stats coverage score';
  gap: var(--unit);
`;
const Stats = styled.div`
  grid-area: stats;
`;
const Coverage = styled.div`
  grid-area: coverage;
  text-align: center;
`;
const Score = styled.div`
  grid-area: score;
  text-align: right;
`;

export type GenericGame = {
  id: string;
  date: Date;
  gameweekDisplayName: string;
  homeTeam: GenericTeam | null;
  awayTeam: GenericTeam | null;
  competition?: { name: string };
  homeScore?: number;
  awayScore?: number;
  status?: GameStatus;
  minutesPlayed?: number;
  coverageStatus?: GameCoverageStatus;
  onClick?: () => void;
  playerScore?: ReactNode;
  playerStats?: ReactNode;
};

type GenericTeam = {
  name: string;
  pictureUrl?: string | null;
  to?: string;
  styleGuideColor?: string | null;
};

export type Props = {
  game: GenericGame;
  hideGameWeek?: boolean;
};

const ButtonWrapper = ({
  onClick,
  children,
  style,
}: {
  onClick: Props['game']['onClick'];
  children: ReactNode;
  style?: CSSProperties;
}) => {
  if (onClick) {
    return (
      <ButtonRoot onClick={onClick} style={style}>
        {children}
      </ButtonRoot>
    );
  }

  return <Root style={style}>{children}</Root>;
};

const StyledLink = styled(Link)`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Team = ({ team, withLink }: { team: GenericTeam; withLink: boolean }) => {
  const content = (
    <Vertical center gap={0.5}>
      <Logo src={team.pictureUrl || defaultShield} alt={team.name} />
      <BodyM as="p">{team.name}</BodyM>
    </Vertical>
  );

  if (team.to && withLink) {
    return <StyledLink to={team.to}>{content}</StyledLink>;
  }

  return content;
};

export const Game = ({ game, hideGameWeek }: Props) => {
  const { sport } = useSportContext();
  const { up: isLaptopAndAbove } = useScreenSize('laptop');
  const fullCoverage =
    !game.coverageStatus || game.coverageStatus === GameCoverageStatus.FULL;

  const isLive = isGameLive(game.status);
  const isFinished = isGameFinished(game.status);

  const isFootball = sport === Sport.FOOTBALL;

  const leftTeam = isFootball ? game.homeTeam : game.awayTeam;
  const rightTeam = isFootball ? game.awayTeam : game.homeTeam;
  const leftScore = isFootball ? game.homeScore : game.awayScore;
  const rightScore = isFootball ? game.awayScore : game.homeScore;

  return (
    <ButtonWrapper
      key={game.id}
      onClick={game.onClick}
      style={
        {
          '--game-background': `
          linear-gradient(to bottom, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)),
          conic-gradient(from 180deg at 50% 50%,
           var(--c-black) 0deg,
          ${game?.homeTeam?.styleGuideColor || 'var(--c-nd-50)'} 90deg,
          var(--c-black) 180deg,
          ${game?.awayTeam?.styleGuideColor || 'var(--c-nd-50)'} 270deg,
          var(--c-black) 360deg
          )
          `,
        } as CSSProperties
      }
    >
      <Overlay />

      <Relative gap={1}>
        <Metadata gap={0.5}>
          <LabelS as="p" color="var(--c-nd-600)">
            <FormattedDate value={game.date} month="short" day="2-digit" />
            {!hideGameWeek && !!game.gameweekDisplayName && (
              <span>
                {' ('}
                {game.gameweekDisplayName})
              </span>
            )}
          </LabelS>
          {!!game.competition && (
            <>
              {!isLaptopAndAbove && (
                <LabelS as="p" color="var(--c-nd-600)">
                  •
                </LabelS>
              )}
              <LabelS as="p" color="var(--c-nd-600)">
                {game.competition.name}
              </LabelS>
            </>
          )}
        </Metadata>
        <StyledHorizontal>
          {!!leftTeam && <Team team={leftTeam} withLink={!game.onClick} />}
          {isLive || isFinished ? (
            <Vertical center gap={0.5}>
              <NoWrapBodyS as="p" bold>
                {leftScore} - {rightScore}
              </NoWrapBodyS>
              {!!isFinished && (
                <Badge>
                  <LabelS as="p">
                    <FormattedMessage
                      {...(isFootball ? fantasy.fullTime : fantasy.final)}
                    />
                  </LabelS>
                </Badge>
              )}
              {!!isLive && (
                <>
                  {game.minutesPlayed ? (
                    <Badge>
                      <Horizontal>
                        <LiveIndicator />
                        <LabelS as="span">{game.minutesPlayed}&apos;</LabelS>
                      </Horizontal>
                    </Badge>
                  ) : (
                    <LiveIndicator />
                  )}
                </>
              )}
            </Vertical>
          ) : (
            <Vertical center>
              <Badge>
                <LabelS as="p">
                  <FormattedTime value={game.date} />
                </LabelS>
              </Badge>
            </Vertical>
          )}
          {!!rightTeam && <Team team={rightTeam} withLink={!game.onClick} />}
        </StyledHorizontal>
        {(!!game.playerScore || !!game.playerStats || !fullCoverage) && (
          <Metadatas>
            <Stats>{game.playerStats}</Stats>
            {!fullCoverage && !!game.coverageStatus && (
              <Coverage>
                <GameCoverageBadge coverageStatus={game.coverageStatus} />
              </Coverage>
            )}
            {!!game.playerScore && <Score>{game.playerScore}</Score>}
          </Metadatas>
        )}
      </Relative>
    </ButtonWrapper>
  );
};
